import {FC, useMemo} from 'react'
import {useQuery} from 'react-query'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import {
  SEND_HEALTH_QUERY_KEY,
  SEND_HEALTH_REFETCH_INTERVAL_MS,
  SEND_HEALTH_STALE_TIME_MS,
  getSendHealth,
  readSendHealthError,
} from './core/_requests'
import {SendHealthRegistration, SendHealthSnapshot} from './core/_models'

/** Persian digits are used everywhere else in the panel, so counts follow suit. */
const formatCount = (value?: number | null) =>
  new Intl.NumberFormat('fa-IR').format(Number(value || 0))

/** Rial amounts are shown in millions to keep the table readable. */
const formatMillions = (value?: number | null) => {
  const millions = Number(value || 0) / 1_000_000
  return new Intl.NumberFormat('fa-IR', {maximumFractionDigits: 0}).format(millions)
}

const EVIDENCE_LABELS: Record<string, string> = {
  no_reference: 'بدون شماره مرجع — ارسال اثبات نشده',
  awaiting_inquiry: 'شماره مرجع دارد، پاسخ استعلام نیامده',
  tax_rejected: 'سازمان قطعاً رد کرده',
  other: 'نیازمند بررسی موردی',
}

const LATENCY_LABELS: Record<string, string> = {
  le_1m: 'تا ۱ دقیقه',
  le_5m: '۱ تا ۵ دقیقه',
  le_15m: '۵ تا ۱۵ دقیقه',
  le_60m: '۱۵ تا ۶۰ دقیقه',
  gt_60m: 'بیش از ۶۰ دقیقه',
  none: 'بدون شماره مرجع',
}

const LATENCY_ORDER = ['le_1m', 'le_5m', 'le_15m', 'le_60m', 'gt_60m', 'none']

/**
 * A brand-neutral categorical ramp. The first colour is reserved for the dominant channel so the
 * chart reads the same way each time the data shifts.
 */
const CHANNEL_COLORS = [
  '#3b82f6',
  '#14b8a6',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#84cc16',
  '#ec4899',
  '#64748b',
]

type ChannelRollup = {
  label: string
  invoices: number
}

/** Businesses ranked by how much they registered, with their channel split. */
type BusinessRollup = {
  companyId: number
  title: string
  total: number
  channels: {label: string; invoices: number}[]
  lastAt?: string | null
}

const rollUpBusinesses = (rows: SendHealthRegistration[]): BusinessRollup[] => {
  const byCompany = new Map<number, BusinessRollup>()

  rows.forEach((row) => {
    const existing = byCompany.get(row.companyId)
    if (existing) {
      existing.total += Number(row.invoices || 0)
      existing.channels.push({label: row.channelLabel, invoices: Number(row.invoices || 0)})
      if (!existing.lastAt || (row.lastAt && row.lastAt > existing.lastAt)) {
        existing.lastAt = row.lastAt
      }
      return
    }

    byCompany.set(row.companyId, {
      companyId: row.companyId,
      title: row.title,
      total: Number(row.invoices || 0),
      channels: [{label: row.channelLabel, invoices: Number(row.invoices || 0)}],
      lastAt: row.lastAt,
    })
  })

  return Array.from(byCompany.values())
    .map((business) => ({
      ...business,
      channels: business.channels.sort((a, b) => b.invoices - a.invoices),
    }))
    .sort((a, b) => b.total - a.total)
}

const SendHealthPanel: FC = () => {
  const {data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching} =
    useQuery<SendHealthSnapshot>(
      SEND_HEALTH_QUERY_KEY,
      () => getSendHealth(),
      {
        refetchInterval: SEND_HEALTH_REFETCH_INTERVAL_MS,
        staleTime: SEND_HEALTH_STALE_TIME_MS,
        refetchOnWindowFocus: false,
      }
    )

  const businesses = useMemo(
    () => rollUpBusinesses(data?.registrations || []),
    [data?.registrations]
  )

  const channelRollup = useMemo<ChannelRollup[]>(
    () =>
      (data?.channels || [])
        .map((channel) => ({label: channel.channelLabel, invoices: Number(channel.invoices || 0)}))
        .sort((a, b) => b.invoices - a.invoices),
    [data?.channels]
  )

  const channelChart = useMemo<{options: ApexOptions; series: number[]}>(() => {
    return {
      series: channelRollup.map((channel) => channel.invoices),
      options: {
        chart: {type: 'donut', fontFamily: 'inherit', animations: {enabled: false}},
        labels: channelRollup.map((channel) => channel.label),
        colors: CHANNEL_COLORS,
        legend: {position: 'bottom', fontSize: '12px'},
        dataLabels: {enabled: false},
        stroke: {width: 0},
        tooltip: {
          y: {formatter: (value: number) => `${formatCount(value)} صورتحساب`},
        },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'جمع ثبت‌شده',
                  formatter: () => formatCount(channelRollup.reduce((s, c) => s + c.invoices, 0)),
                },
              },
            },
          },
        },
        noData: {text: 'در این بازه صورتحسابی ثبت نشده است.'},
      },
    }
  }, [channelRollup])

  const latencyChart = useMemo<{options: ApexOptions; series: ApexAxisChartSeries}>(() => {
    const byBucket = new Map((data?.latency || []).map((row) => [row.bucket, row.invoices]))
    const categories = LATENCY_ORDER.filter((key) => byBucket.has(key))

    return {
      series: [
        {
          name: 'صورتحساب',
          data: categories.map((key) => Number(byBucket.get(key) || 0)),
        },
      ],
      options: {
        chart: {type: 'bar', fontFamily: 'inherit', toolbar: {show: false}, animations: {enabled: false}},
        plotOptions: {bar: {horizontal: true, borderRadius: 4, barHeight: '60%'}},
        colors: ['#14b8a6'],
        dataLabels: {
          enabled: true,
          formatter: (value: number) => formatCount(value),
          style: {fontSize: '11px'},
        },
        xaxis: {
          categories: categories.map((key) => LATENCY_LABELS[key] || key),
          labels: {formatter: (value: string) => formatCount(Number(value))},
        },
        grid: {strokeDashArray: 4},
        tooltip: {y: {formatter: (value: number) => `${formatCount(value)} صورتحساب`}},
        noData: {text: 'داده‌ای برای این بازه نیست.'},
      },
    }
  }, [data?.latency])

  if (isLoading) {
    return (
      <div className='card'>
        <div className='card-body d-flex align-items-center justify-content-center py-20'>
          <span className='spinner-border spinner-border-sm text-primary me-3' role='status' />
          <span className='text-muted'>در حال خواندن وضعیت ارسال و استعلام…</span>
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='alert alert-danger d-flex flex-column mb-0' role='alert'>
            <span className='fw-bold mb-2'>وضعیت ارسال خوانده نشد</span>
            <span className='mb-3'>
              {readSendHealthError(error, 'ارتباط با سرور برقرار نشد.')}
            </span>
            <button type='button' className='btn btn-sm btn-light-danger align-self-start' onClick={() => refetch()}>
              تلاش مجدد
            </button>
          </div>
        </div>
      </div>
    )
  }

  const {buckets, sla} = data
  const slaHealthy = Number(sla?.breaches || 0) === 0
  const humanQueues = (data.queues || []).filter((queue) => queue.needsHuman)
  const humanQueueMessages = humanQueues.reduce((sum, queue) => sum + Number(queue.messages || 0), 0)

  return (
    <div className='d-flex flex-column gap-5'>
      {/* headline: the SLA the operator set, and the bucket the old dashboard hid */}
      <div className='row g-5'>
        <div className='col-12 col-lg-4'>
          <div className={`card h-100 ${slaHealthy ? 'border-success' : 'border-danger'} border border-2`}>
            <div className='card-body'>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <span className='text-muted fs-7'>
                  بدون شماره مرجع بیش از {formatCount(sla?.thresholdMinutes)} دقیقه
                </span>
                <i className={`las ${slaHealthy ? 'la-check-circle text-success' : 'la-exclamation-triangle text-danger'} fs-2`} />
              </div>
              <div className={`fs-2hx fw-bold ${slaHealthy ? 'text-success' : 'text-danger'}`}>
                {formatCount(sla?.breaches)}
              </div>
              <div className='text-muted fs-7 mt-2'>
                {slaHealthy
                  ? 'همهٔ ارسال‌ها داخل مهلت شماره مرجع گرفته‌اند.'
                  : `در ${formatCount(sla?.distinctCompanies)} کسب‌وکار`}
              </div>
            </div>
          </div>
        </div>

        <div className='col-12 col-lg-4'>
          <div className='card h-100'>
            <div className='card-body'>
              <div className='text-muted fs-7 mb-2'>استعلام نا مشخص</div>
              <div className='fs-2hx fw-bold text-warning'>{formatCount(buckets?.unknownCount)}</div>
              <div className='text-muted fs-7 mt-2'>
                ارسال شده‌اند و شماره مرجع دارند، ولی پاسخ قطعی سازمان ثبت نشده.
              </div>
            </div>
          </div>
        </div>

        <div className='col-12 col-lg-4'>
          <div className='card h-100'>
            <div className='card-body'>
              <div className='text-muted fs-7 mb-2'>در صف ارسال</div>
              <div className='fs-2hx fw-bold text-primary'>{formatCount(buckets?.pendingCount)}</div>
              <div className='text-muted fs-7 mt-2'>
                ثبت شده در سامانه: {formatCount(buckets?.successCount)}، ناموفق:{' '}
                {formatCount(buckets?.erroredCount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* what the operator asked for: who registered how much, through which channel */}
      <div className='card'>
        <div className='card-header align-items-center'>
          <div>
            <h3 className='card-title mb-1'>ثبت صورتحساب به تفکیک کسب‌وکار و روش</h3>
            <span className='text-muted fs-7'>
              {formatCount(data.registrationWindowHours)} ساعت گذشته · آخرین به‌روزرسانی{' '}
              {data.generatedAtTehran}
            </span>
          </div>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'در حال به‌روزرسانی…' : 'به‌روزرسانی'}
            </button>
          </div>
        </div>
        <div className='card-body'>
          <div className='row g-5'>
            <div className='col-12 col-xl-5'>
              <ReactApexChart
                options={channelChart.options}
                series={channelChart.series}
                type='donut'
                height={300}
              />
            </div>
            <div className='col-12 col-xl-7'>
              {businesses.length === 0 ? (
                <div className='text-muted text-center py-10'>
                  در این بازه هیچ صورتحسابی ثبت نشده است.
                </div>
              ) : (
                <div className='table-responsive mp-table-cards-box'>
                  <table className='table table-row-dashed align-middle gs-0 gy-3 mp-table-cards'>
                    <thead>
                      <tr className='fw-bold text-muted fs-7'>
                        <th>کسب‌وکار</th>
                        <th className='text-center'>تعداد</th>
                        <th>روش ثبت</th>
                      </tr>
                    </thead>
                    <tbody>
                      {businesses.map((business) => (
                        <tr key={business.companyId}>
                          <td className='mp-table-cards__lead'>
                            {/* One block: a card's cell lays its children out in a row. */}
                            <div>
                              <span className='fw-semibold d-block'>{business.title}</span>
                              <span className='text-muted fs-8'>
                                شناسه <bdi dir='ltr'>{business.companyId}</bdi>
                              </span>
                            </div>
                          </td>
                          <td className='text-center fw-bold' data-label='تعداد'>
                            {formatCount(business.total)}
                          </td>
                          <td data-label='روش ثبت'>
                            <div className='d-flex flex-wrap gap-2'>
                              {business.channels.map((channel) => (
                                <span
                                  key={`${business.companyId}-${channel.label}`}
                                  className='badge badge-light-primary'
                                >
                                  {channel.label}: {formatCount(channel.invoices)}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* why things are stuck, and how fast the pipeline actually is */}
      <div className='row g-5'>
        <div className='col-12 col-xl-6'>
          <div className='card h-100'>
            <div className='card-header'>
              <h3 className='card-title mb-0'>علت معلق‌ماندن</h3>
            </div>
            <div className='card-body'>
              {(data.evidence || []).length === 0 ? (
                <div className='text-muted text-center py-10'>صورتحساب معلقی وجود ندارد.</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle gs-0 gy-3 mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted fs-7'>
                        <th>وضعیت</th>
                        <th className='text-center'>فاز</th>
                        <th className='text-center'>تعداد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.evidence
                        .slice()
                        .sort((a, b) => b.invoices - a.invoices)
                        .map((row) => (
                          <tr key={`${row.phase}-${row.evidence}`}>
                            <td>{EVIDENCE_LABELS[row.evidence] || row.evidence}</td>
                            <td className='text-center'>{formatCount(row.phase)}</td>
                            <td className='text-center fw-bold'>{formatCount(row.invoices)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='card h-100'>
            <div className='card-header'>
              <div>
                <h3 className='card-title mb-1'>سرعت گرفتن شماره مرجع</h3>
                <span className='text-muted fs-7'>
                  از لحظهٔ اقدام به ارسال، {formatCount(data.latencyWindowDays)} روز گذشته
                </span>
              </div>
            </div>
            <div className='card-body'>
              <ReactApexChart
                options={latencyChart.options}
                series={latencyChart.series}
                type='bar'
                height={280}
              />
            </div>
          </div>
        </div>
      </div>

      {/*
        Why the organisation rejected them, by its own code. «۳۴۶ رد شده» is not something an
        operator can act on; «کد ۰۴۱۱۱ در یک کسب‌وکار» is — that one is a missing trusted-company
        contract for the whole fiscal memory, and resending never fixes it.
      */}
      {(data.rejections || []).length > 0 && (
        <div className='card'>
          <div className='card-header'>
            <div>
              <h3 className='card-title mb-1'>علت رد شدن از سوی سازمان</h3>
              <span className='text-muted fs-7'>
                کدی که در یک کسب‌وکار تکرار می‌شود، مشکل تنظیمات آن کسب‌وکار است نه خودِ
                صورتحساب‌ها
              </span>
            </div>
          </div>
          <div className='card-body'>
            <div className='table-responsive mp-table-cards-box'>
              <table className='table table-row-dashed align-middle gs-0 gy-3 mb-0 mp-table-cards'>
                <thead>
                  <tr className='fw-bold text-muted fs-7'>
                    <th>کد</th>
                    <th>پیام سازمان</th>
                    <th className='text-center'>صورتحساب</th>
                    <th className='text-center'>کسب‌وکار</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rejections
                    .slice()
                    .sort((a, b) => b.invoices - a.invoices)
                    .map((row) => (
                      <tr key={`${row.code}-${row.message}`}>
                        <td className='mp-table-cards__lead'>
                          {row.code ? (
                            <span className='badge badge-light-danger font-monospace' dir='ltr'>
                              {row.code}
                            </span>
                          ) : (
                            <span className='text-muted fs-8'>بدون کد</span>
                          )}
                        </td>
                        <td className='fs-7' data-label='پیام سازمان'>
                          {row.message}
                        </td>
                        <td className='text-center fw-bold' data-label='صورتحساب'>
                          {formatCount(row.invoices)}
                        </td>
                        <td className='text-center' data-label='کسب‌وکار'>
                          {formatCount(row.businesses)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* queues that nothing drains: these need a human, by design */}
      <div className='card'>
        <div className='card-header'>
          <div>
            <h3 className='card-title mb-1'>صف‌ها</h3>
            <span className='text-muted fs-7'>
              {humanQueueMessages > 0
                ? `${formatCount(humanQueueMessages)} پیام در صف‌های بازبینی دستی که هیچ مصرف‌کننده‌ای ندارند`
                : 'هیچ پیامی منتظر بازبینی دستی نیست'}
            </span>
          </div>
        </div>
        <div className='card-body'>
          {data.queueError && (
            <div className='alert alert-warning fs-7' role='alert'>
              وضعیت صف‌ها خوانده نشد: {data.queueError}
            </div>
          )}
          {(data.queues || []).length === 0 ? (
            <div className='text-muted text-center py-8'>صفی با پیام یا مصرف‌کننده یافت نشد.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gs-0 gy-3 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted fs-7'>
                    <th>خانوادهٔ صف</th>
                    <th className='text-center'>صف</th>
                    <th className='text-center'>پیام</th>
                    <th className='text-center'>مصرف‌کننده</th>
                  </tr>
                </thead>
                <tbody>
                  {data.queues.map((queue) => (
                    <tr key={queue.family}>
                      <td>
                        <span className='font-monospace fs-8'>{queue.family}</span>
                        {queue.needsHuman && (
                          <span className='badge badge-light-warning ms-2'>بازبینی دستی</span>
                        )}
                      </td>
                      <td className='text-center'>{formatCount(queue.queues)}</td>
                      <td className={`text-center fw-bold ${queue.messages > 0 && queue.consumers === 0 ? 'text-danger' : ''}`}>
                        {formatCount(queue.messages)}
                      </td>
                      <td className='text-center'>{formatCount(queue.consumers)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className='text-muted fs-8 text-center'>
        آخرین خواندهٔ موفق: {new Date(dataUpdatedAt).toLocaleTimeString('fa-IR')}؛ عکس وضعیت تا ۵
        دقیقه کش می‌شود
      </div>
    </div>
  )
}

export {SendHealthPanel}
export default SendHealthPanel
